/**
 * 作品展示API
 */
import axios from "axios";
import axiosInstance from "../services/axiosConfig";

const API_BASE_URL =
  process.env.REACT_APP_BASE_API_URL || "http://localhost:5001/api/v1";

/**
 * 获取作品列表
 * @param {Object} params - 查询参数
 * @param {number} params.page - 页码
 * @param {number} params.page_size - 每页数量
 * @param {string} params.category - 分类
 * @param {string} params.difficulty - 难度
 * @param {boolean} params.is_featured - 是否精选
 * @param {string} params.search - 搜索关键词
 * @param {string} params.sort_by - 排序字段
 * @param {string} params.sort_order - 排序方式
 * @returns {Promise}
 */
export const getShowcaseList = async (params = {}) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/showcase/list`, {
      params: {
        page: params.page || 1,
        page_size: params.page_size || 12,
        category: params.category,
        difficulty: params.difficulty,
        is_featured: params.is_featured,
        search: params.search,
        sort_by: params.sort_by || "created_at",
        sort_order: params.sort_order || "desc",
      },
    });
    return response.data;
  } catch (error) {
    console.error("获取作品列表失败:", error);
    throw error;
  }
};

/**
 * 获取指定用户的作品列表
 * @param {number} userId - 用户ID
 * @param {Object} params - 查询参数
 * @returns {Promise}
 */
export const getShowcaseListByUser = async (userId, params = {}) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/showcase/list/by-user/${userId}`, {
      params: {
        page: params.page || 1,
        page_size: params.page_size || 12,
        sort_by: params.sort_by || "created_at",
        sort_order: params.sort_order || "desc",
      },
    });
    return response.data;
  } catch (error) {
    console.error(`获取用户 ${userId} 的作品列表失败:`, error);
    throw error;
  }
};

/**
 * 获取作品详情
 * @param {number} showcaseId - 作品ID
 * @returns {Promise}
 */
export const getShowcaseDetail = async (showcaseId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/showcase/${showcaseId}`);
    return response.data;
  } catch (error) {
    console.error("获取作品详情失败:", error);
    throw error;
  }
};

/**
 * 点赞作品
 * @param {number} showcaseId - 作品ID
 * @returns {Promise}
 */
export const likeShowcase = async (showcaseId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/showcase/${showcaseId}/like`
    );
    return response.data;
  } catch (error) {
    console.error("点赞失败:", error);
    throw error;
  }
};

/**
 * 获取分类列表
 * @returns {Promise}
 */
export const getCategories = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/showcase/categories/list`
    );
    return response.data;
  } catch (error) {
    console.error("获取分类列表失败:", error);
    throw error;
  }
};

/**
 * 创建作品（管理员接口）
 * @param {Object} showcaseData - 作品数据
 * @returns {Promise}
 */
export const createShowcase = async (showcaseData) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}/showcase/create`,
      showcaseData
    );
    return response;
  } catch (error) {
    console.error("创建作品失败:", error);
    throw error;
  }
};

/**
 * 更新作品（管理员接口）
 * @param {number} showcaseId - 作品ID
 * @param {Object} showcaseData - 作品数据
 * @returns {Promise}
 */
export const updateShowcase = async (showcaseId, showcaseData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/showcase/${showcaseId}`,
      showcaseData
    );
    return response.data;
  } catch (error) {
    console.error("更新作品失败:", error);
    throw error;
  }
};

/**
 * 删除作品（管理员接口）
 * @param {number} showcaseId - 作品ID
 * @returns {Promise}
 */
export const deleteShowcase = async (showcaseId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/showcase/${showcaseId}`
    );
    return response.data;
  } catch (error) {
    console.error("删除作品失败:", error);
    throw error;
  }
};

/**
 * 下载带水印的作品视频
 * @param {number} showcaseId - 作品ID
 * @param {string} watermarkText - 水印文本
 * @returns {Promise<Blob>}
 */
export const downloadShowcaseWithWatermark = async (
  showcaseId,
  watermarkText
) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/showcase/${showcaseId}/download`,
      { watermark_text: watermarkText },
      { responseType: "blob" }
    );
    return response.data;
  } catch (error) {
    console.error("下载作品视频失败:", error);
    throw error;
  }
};

/**
 * 保存生成的作品 (HTML/GGB)
 * @param {Object} data - { code, type, prompt, title, created_by }
 * @returns {Promise}
 */
export const saveGeneratedShowcase = async (data) => {
  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}/showcase/save-generated`,
      data
    );
    return response;
  } catch (error) {
    console.error("保存作品失败:", error);
    throw error;
  }
};

/**
 * 更新作品缩略图
 * @param {number} showcaseId - 作品ID
 * @param {string} thumbnailBase64 - Base64格式的图片
 * @returns {Promise}
 */
export const updateShowcaseThumbnail = async (showcaseId, thumbnailBase64) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/showcase/${showcaseId}/thumbnail`,
      { thumbnail: thumbnailBase64 }
    );
    return response.data;
  } catch (error) {
    console.error("更新缩略图失败:", error);
    throw error;
  }
};

/**
 * 获取作品提示词（仅限作者或已购买用户）
 * @param {number} showcaseId - 作品ID
 * @param {number} userId - 当前用户ID
 * @returns {Promise}
 */
export const getShowcasePrompt = async (showcaseId, userId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/showcase/${showcaseId}/prompt`,
      { params: { user_id: userId } }
    );
    return response.data;
  } catch (error) {
    // 403 表示未授权（未购买），不打印错误
    if (error.response && error.response.status === 403) {
      return { success: false, message: "请先购买后再查看提示词" };
    }
    console.error("获取提示词失败:", error);
    throw error;
  }
};

/**
 * 上传作品的用户输入图片（存储到OSS）
 * 当用户在提交提示词时上传了图片，调用此接口将图片与作品关联
 * @param {number} showcaseId - 作品ID
 * @param {string} imageBase64 - Base64格式的图片（可含 data:image/...;base64, 前缀）
 * @returns {Promise}
 */
export const updateShowcaseInputImage = async (showcaseId, imageBase64) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/showcase/${showcaseId}/input-image`,
      { thumbnail: imageBase64 }
    );
    return response.data;
  } catch (error) {
    console.error("上传输入图片失败:", error);
    throw error;
  }
};

/**
 * 批量上传作品的用户输入图片（支持多张，存储到OSS）
 * @param {number} showcaseId - 作品ID
 * @param {string[]} imageBase64List - Base64格式的图片数组（可含 data:image/...;base64, 前缀）
 * @returns {Promise}
 */
export const updateShowcaseInputImages = async (showcaseId, imageBase64List) => {
  try {
    const thumbnails = Array.isArray(imageBase64List) ? imageBase64List : [imageBase64List];
    const response = await axios.post(
      `${API_BASE_URL}/showcase/${showcaseId}/input-image`,
      { thumbnails }
    );
    return response.data;
  } catch (error) {
    console.error("批量上传输入图片失败:", error);
    throw error;
  }
};

/**
 * 用户重命名自己的作品
 * @param {number} showcaseId - 作品ID
 * @param {number} userId - 当前用户ID
 * @param {string} title - 新标题
 * @returns {Promise}
 */
export const renameOwnedShowcase = async (showcaseId, userId, title) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/showcase/${showcaseId}/rename`,
      { user_id: userId, title }
    );
    return response.data;
  } catch (error) {
    console.error("重命名作品失败:", error);
    throw error;
  }
};

/**
 * 付费解锁作品提示词（消耗5积分，作者获得2积分奖励）
 * @param {number} showcaseId - 作品ID
 * @param {number} userId - 当前用户ID
 * @returns {Promise}
 */
export const unlockShowcasePrompt = async (showcaseId, userId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/showcase/${showcaseId}/unlock-prompt`,
      { user_id: userId }
    );
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      return { success: false, message: error.response.data.detail || "解锁失败" };
    }
    console.error("解锁提示词失败:", error);
    throw error;
  }
};

// 客户端内存缓存，避免用户短时间内重复查询相同提示词产生多余网络请求
const recommendationClientCache = new Map();
const RECOMMENDATION_CLIENT_TTL = 10 * 60 * 1000; // 10分钟

/**
 * 根据提示词获取智能推荐作品（多层次索引优化）
 * @param {Object} params
 * @param {string} params.prompt - 用户输入的提示词
 * @param {string} [params.mode] - 当前模式 (manim, threejs, geogebra, solve, qq)
 * @param {number} [params.limit=6] - 推荐条数
 * @param {number} [params.userId] - 当前登录用户ID（仅用于客户端缓存隔离）
 * @param {string} [params.apiKey] - 用于后端安全识别用户的 API Key
 * @returns {Promise<{success: boolean, data: {items: Array, keywords: Array}}>}
 */
export const getRecommendedShowcases = async ({
  prompt = "",
  mode = null,
  limit = 6,
  userId = null,
  apiKey = null,
} = {}) => {
  const cleanPrompt = (prompt || "").trim();
  const cacheKey = `${userId || "anonymous"}:${mode || "all"}:${cleanPrompt}:${limit}`;
  const cached = recommendationClientCache.get(cacheKey);

  if (cached && Date.now() - cached.timestamp < RECOMMENDATION_CLIENT_TTL) {
    return cached.data;
  }

  try {
    const response = await axios.get(`${API_BASE_URL}/showcase/recommendations`, {
      params: {
        prompt: cleanPrompt,
        mode: mode || undefined,
        limit: limit || 6,
      },
      headers: apiKey ? { Authorization: `Bearer ${apiKey}` } : undefined,
      timeout: 5000,
    });

    if (response.data && response.data.success) {
      recommendationClientCache.set(cacheKey, {
        timestamp: Date.now(),
        data: response.data,
      });
      return response.data;
    }
    return response.data;
  } catch (error) {
    console.warn("推荐作品智能接口调用失败，自动降级到精选列表:", error);
    // 降级策略：请求精选作品，保障前端展示永不崩溃
    try {
      const fallbackRes = await getShowcaseList({
        page: 1,
        page_size: limit || 6,
        is_featured: true,
        sort_by: "view_count",
        sort_order: "desc",
      });
      if (fallbackRes?.success) {
        return {
          success: true,
          data: {
            items: fallbackRes.data.items || [],
            keywords: [],
            mode,
            isFallback: true,
          },
        };
      }
    } catch (fallbackError) {
      console.error("降级获取精选作品也失败:", fallbackError);
    }
    return {
      success: false,
      data: { items: [], keywords: [], mode },
    };
  }
};

